import { INpsSummary } from '../models/nps-summary.model';
import { ISeriesPoint } from '../models/series-point.model';

/** Fila de la tabla "NPS - Detalle de radiografia por estado". */
export interface IEstadoNpsRow {
  estado: string;
  detractores: number;
  pasivos: number;
  promotores: number;
  nps: number;
  /** Placeholder de la columna HISTORICO (la celda se pinta como icono). */
  historico: string;
}

/** Punto del mapa: `name` debe coincidir con el estado del GeoJSON. */
export interface IMapaEstado {
  name: string;
  value: number;
}

/* -------------------------------------------------------------------------- */
/*                             ANCHOR NPS General                             */
/* -------------------------------------------------------------------------- */

export const NPS_GENERAL: INpsSummary = {
  nps: 74,
  detractor: 5,
  pasivo: 16,
  promotor: 79,
  period: 'Agosto/2026',
};

/**
 * Trece meses del area teal del dashboard: Agosto/2025 a Agosto/2026.
 *
 * Es el NPS **del mes**, no la ventana movil, y por eso su ultimo punto es el
 * mismo numero que la dona de arriba. La ventana movil vive en `NPS_MENSUAL`
 * de `home.data.ts`, que es otra grafica y otra pregunta.
 */
export const NPS_HISTORICO: Array<ISeriesPoint> = [
  { label: 'Agosto',     value: 65,  count: 7240 },
  { label: 'Septiembre', value: 66,  count: 6950 },
  { label: 'Octubre',    value: 64,  count: 7420 },
  { label: 'Noviembre',  value: 67,  count: 7810 },
  { label: 'Diciembre',  value: 68,  count: 8260 },
  { label: 'Enero',      value: 66,  count: 6130 },
  { label: 'Febrero',    value: 69,  count: 7050 },
  { label: 'Marzo',      value: 70,  count: 7690 },
  { label: 'Abril',      value: 71,  count: 8020 },
  { label: 'Mayo',       value: 69,  count: 7480 },
  { label: 'Junio',      value: 72,  count: 8310 },
  { label: 'Julio',      value: 73,  count: 8640 },
  { label: 'Agosto',     value: 74,  count: 3148 },
];

/* -------------------------------------------------------------------------- */
/*                       ANCHOR Radiografia por estado                        */
/* -------------------------------------------------------------------------- */

export const INDICADORES: Array<string> = ['NPS'];

export const MAPA_NPS: Array<IMapaEstado> = [
  { name: 'Ciudad de México',  value: 76 },
  { name: 'México',            value: 74 },
  { name: 'Guanajuato',        value: 71 },
  { name: 'Guerrero',          value: 82 },
  { name: 'Hidalgo',           value: 66 },
  { name: 'Jalisco',           value: 79 },
  { name: 'Michoacán',         value: 64 },
  { name: 'Morelos',           value: 81 },
  { name: 'Puebla',            value: 69 },
  { name: 'Querétaro',         value: 75 },
];

export const ESTADOS_NPS: Array<IEstadoNpsRow> = [
  { estado: 'CDMX',              detractores:  5, pasivos: 14, promotores: 81, nps: 76, historico: '' },
  { estado: 'ESTADO DE MEXICO',  detractores:  8, pasivos: 10, promotores: 82, nps: 74, historico: '' },
  { estado: 'GUANAJUATO',        detractores:  6, pasivos: 17, promotores: 77, nps: 71, historico: '' },
  { estado: 'GUERRERO',          detractores:  4, pasivos: 10, promotores: 86, nps: 82, historico: '' },
  { estado: 'HIDALGO',           detractores:  8, pasivos: 18, promotores: 74, nps: 66, historico: '' },
  { estado: 'JALISCO',           detractores:  6, pasivos:  9, promotores: 85, nps: 79, historico: '' },
  { estado: 'MICHOACAN',         detractores:  5, pasivos: 26, promotores: 69, nps: 64, historico: '' },
  { estado: 'MORELOS',           detractores:  5, pasivos:  9, promotores: 86, nps: 81, historico: '' },
  { estado: 'PUEBLA',            detractores:  9, pasivos: 13, promotores: 78, nps: 69, historico: '' },
  { estado: 'QUERETARO',         detractores:  4, pasivos: 17, promotores: 79, nps: 75, historico: '' },
];

/* -------------------------------------------------------------------------- */
/*                        ANCHOR Historico por estado                         */
/* -------------------------------------------------------------------------- */

/** Diecinueve meses: Febrero/2025 a Agosto/2026. */
const MESES_HISTORICO: Array<string> = [
  'Feb-25', 'Mar-25', 'Abr-25', 'May-25', 'Jun-25', 'Jul-25', 'Ago-25',
  'Sep-25', 'Oct-25', 'Nov-25', 'Dic-25', 'Ene-26', 'Feb-26', 'Mar-26',
  'Abr-26', 'May-26', 'Jun-26', 'Jul-26', 'Ago-26',
];

const serie = (valores: Array<number>): Array<ISeriesPoint> =>
  valores.map((value, index) => ({ label: MESES_HISTORICO[index] ?? '', value }));

/** Serie del modal que abre la columna HISTORICO, indexada por nombre de estado. */
export const HISTORICO_POR_ESTADO: Record<string, Array<ISeriesPoint>> = {
  'CDMX': serie([
    62, 66, 60, 64, 66, 66, 69, 67, 68, 71,
    70, 69, 72, 73, 72, 71, 73, 73, 76,
  ]),
  'ESTADO DE MEXICO': serie([
    62, 62, 60, 61, 64, 65, 66, 67, 65, 67,
    66, 67, 68, 70, 71, 69, 70, 72, 74,
  ]),
  'GUANAJUATO': serie([
    56, 58, 59, 60, 62, 58, 63, 65, 60, 62,
    65, 61, 67, 67, 66, 64, 68, 71, 71,
  ]),
  'GUERRERO': serie([
    71, 70, 68, 69, 74, 70, 72, 73, 73, 73,
    75, 73, 76, 76, 78, 76, 82, 79, 82,
  ]),
  'HIDALGO': serie([
    54, 55, 52, 55, 57, 54, 56, 60, 55, 60,
    60, 60, 62, 63, 61, 63, 64, 64, 66,
  ]),
  'JALISCO': serie([
    64, 65, 64, 68, 69, 67, 71, 70, 70, 71,
    75, 71, 73, 77, 74, 74, 77, 79, 79,
  ]),
  'MICHOACAN': serie([
    53, 50, 48, 53, 56, 51, 55, 54, 52, 57,
    59, 55, 57, 61, 62, 58, 63, 62, 64,
  ]),
  'MORELOS': serie([
    66, 69, 66, 69, 73, 70, 71, 73, 69, 73,
    75, 72, 77, 78, 76, 78, 80, 78, 81,
  ]),
  'PUEBLA': serie([
    54, 58, 55, 60, 57, 59, 59, 63, 58, 62,
    64, 61, 64, 65, 68, 62, 69, 68, 69,
  ]),
  'QUERETARO': serie([
    64, 63, 59, 62, 63, 63, 67, 66, 66, 67,
    68, 68, 71, 71, 74, 72, 75, 73, 75,
  ]),
};
